// The bits Chart.js doesn't draw for us, as one inline plugin: the threshold
// status line, the measured→predicted divider, the legacy last-value label,
// and the in-slice percentages on an arc chart.
//
// Deliberately not the annotation or datalabels plugins — both are real bundle
// weight for a CDN-embedded widget, and this is ~100 lines of canvas. Every
// colour comes in as an already-resolved token, so no hex is written here.

import type { Chart, Plugin } from 'chart.js'

import type { ChartColors } from './chartOptions'
import { withAlpha } from './chartTheme'
import { formatValue, pickReadable, type ChartSpec } from './chartSpec'

export interface DecorExtras {
  /** Vertical dashed divider — the measured→predicted "now" boundary. */
  nowX?: number | null
  /** Right-aligned dashed status line with a label. */
  threshold?: { y: number; label: string } | null
  /** The band being dragged out for a zoom, in DATA x coordinates. */
  selection?: { from: number; to: number } | null
}

const ARC = new Set(['pie', 'doughnut'])

export function nwDecor(spec: ChartSpec, colors: ChartColors, extras: DecorExtras = {}): Plugin {
  const threshold = extras.threshold ?? (spec.threshold != null
    ? { y: spec.threshold, label: `${spec.threshold}${spec.unit}` }
    : null)
  const nowX = extras.nowX ?? null

  return {
    id: 'nwDecor',
    afterDatasetsDraw(chart: Chart) {
      const { ctx, chartArea, scales } = chart
      if (!chartArea) return
      ctx.save()

      if (ARC.has(spec.type)) {
        drawArcLabels(chart, spec, colors)
        ctx.restore()
        return
      }

      // The band the reader is dragging out, drawn under everything else so
      // the lines stay readable through it.
      const selection = extras.selection
      if (selection && scales.x) {
        const a = scales.x.getPixelForValue(selection.from)
        const b = scales.x.getPixelForValue(selection.to)
        const left = Math.max(chartArea.left, Math.min(a, b))
        const right = Math.min(chartArea.right, Math.max(a, b))
        if (right > left) {
          ctx.fillStyle = withAlpha(colors.ink, 0.1)
          ctx.fillRect(left, chartArea.top, right - left, chartArea.bottom - chartArea.top)
          ctx.strokeStyle = withAlpha(colors.ink, 0.35)
          ctx.lineWidth = 1
          ctx.beginPath()
          ctx.moveTo(left, chartArea.top)
          ctx.lineTo(left, chartArea.bottom)
          ctx.moveTo(right, chartArea.top)
          ctx.lineTo(right, chartArea.bottom)
          ctx.stroke()
        }
      }

      // The threshold rides the VALUE axis, which swaps sides on a horizontal
      // bar chart — drawing it on y there would put it across the categories.
      const valueScale = spec.horizontal ? scales.x : scales.y
      if (threshold && valueScale && threshold.y <= (valueScale.max as number)) {
        const pos = valueScale.getPixelForValue(threshold.y)
        ctx.strokeStyle = colors.threshold
        ctx.lineWidth = 1.2
        ctx.setLineDash([4, 3])
        ctx.beginPath()
        if (spec.horizontal) {
          ctx.moveTo(pos, chartArea.top)
          ctx.lineTo(pos, chartArea.bottom)
        } else {
          ctx.moveTo(chartArea.left, pos)
          ctx.lineTo(chartArea.right, pos)
        }
        ctx.stroke()
        ctx.setLineDash([])
        ctx.fillStyle = colors.threshold
        ctx.font = '600 10px sans-serif'
        if (spec.horizontal) {
          ctx.textAlign = 'center'
          ctx.fillText(threshold.label, pos, chartArea.top - 2)
        } else {
          ctx.textAlign = 'right'
          ctx.fillText(threshold.label, chartArea.right, pos - 4)
        }
      }

      if (nowX != null && scales.x) {
        const x = scales.x.getPixelForValue(nowX)
        if (x >= chartArea.left && x <= chartArea.right) {
          ctx.strokeStyle = colors.grid
          ctx.lineWidth = 1
          ctx.setLineDash([2, 3])
          ctx.beginPath()
          ctx.moveTo(x, chartArea.top)
          ctx.lineTo(x, chartArea.bottom)
          ctx.stroke()
          ctx.setLineDash([])
        }
      }

      if (spec.lastValueLabel && spec.series[0]?.points.length && scales.x && scales.y) {
        const points = spec.series[0].points
        const last = points[points.length - 1]
        const px = scales.x.getPixelForValue(last.x)
        const py = scales.y.getPixelForValue(last.y)
        ctx.fillStyle = colors.ink
        ctx.font = '700 11px sans-serif'
        ctx.textAlign = px > chartArea.right - 40 ? 'right' : 'center'
        ctx.fillText(formatValue(last.y, spec.unit), px, Math.max(py - 8, chartArea.top + 10))
      }

      ctx.restore()
    },
  }
}

/** Percentages inside the slices big enough to hold them; small ones keep the
 *  tooltip as their only label rather than colliding with their neighbours. */
function drawArcLabels(chart: Chart, spec: ChartSpec, colors: ChartColors) {
  const meta = chart.getDatasetMeta(0)
  const values = (spec.series[0]?.data ?? []).map((v) => v ?? 0)
  const total = values.reduce((a, b) => a + b, 0)
  if (!total) return

  const { ctx } = chart
  ctx.font = '700 10px sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  meta.data.forEach((arc, i) => {
    const share = values[i] / total
    if (share < 0.05) return
    const { x, y } = (arc as unknown as { tooltipPosition: () => { x: number; y: number } }).tooltipPosition()
    const fill = colors.series[i % colors.series.length]
    ctx.fillStyle = pickReadable(fill, colors.surface, colors.ink)
    ctx.fillText(`${Math.round(share * 100)}%`, x, y)
  })

  if (spec.type === 'doughnut' && spec.unit !== '%') {
    ctx.fillStyle = colors.ink
    ctx.font = '700 15px sans-serif'
    ctx.fillText(
      formatValue(total, spec.unit),
      (chart.chartArea.left + chart.chartArea.right) / 2,
      (chart.chartArea.top + chart.chartArea.bottom) / 2,
    )
  }
}
